<template>
  <!--
    Значок сортировки в шапке колонки. Вид зависит от типа данных колонки:
      String  - символьный (A-Z),
      Number  - числовой (0-1),
      Boolean, DateTime - логический (полоски),
      остальные (General/неизвестный) - обычный двухстрелочный значок.

    У типизированных значков стрелка всегда смотрит вниз, а направление сортировки
    (order: 2 возрастание, 3 убывание) показывается порядком правого глифа: цифры
    0/1, буквы A/Z или полоски меняются местами по вертикали. Цвет: зелёный, когда
    сортировка активна (order 2/3), серый, когда её нет (order 1). У двухстрелочного
    (универсального) значка стрелка не фиксирована, подсвечивается активное направление.
  -->
  <svg
    v-if="isTyped"
    class="qtl-sort-icon"
    :class="active ? 'qtl-sort-icon--active' : 'qtl-sort-icon--idle'"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <!-- стрелка направления: всегда вниз, общая для всех типизированных значков.
         Возрастание/убывание различаем не стрелкой, а порядком правого глифа. -->
    <path d="M2 10.6667L4.66667 13.3334L7.33333 10.6667" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M4.66663 13.3334V2.66669" stroke-linecap="round" stroke-linejoin="round"/>

    <!-- символьный (String): буквы A и Z. Возрастание - A сверху, Z снизу; убывание -
         меняем их местами по вертикали (сдвиг между верхней и нижней зоной глифа). -->
    <template v-if="dataType === DT.String">
      <g :transform="isDesc ? 'translate(0, 6.6666)' : ''">
        <path d="M13.3333 5.33331H10" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M10 6.66669V4.33335C10 3.89133 10.1756 3.4674 10.4882 3.15484C10.8007 2.84228 11.2246 2.66669 11.6667 2.66669C12.1087 2.66669 12.5326 2.84228 12.8452 3.15484C13.1577 3.4674 13.3333 3.89133 13.3333 4.33335V6.66669" stroke-linecap="round" stroke-linejoin="round"/>
      </g>
      <g :transform="isDesc ? 'translate(0, -6.6666)' : ''">
        <path d="M10 9.33331H13.3333L10 13.3333H13.3333" stroke-linecap="round" stroke-linejoin="round"/>
      </g>
    </template>

    <!-- числовой (Number): цифры 0 и 1. Возрастание - 0 сверху, 1 снизу; убывание -
         меняем их местами по вертикали (сдвиг между верхней и нижней зоной глифа). -->
    <template v-else-if="dataType === DT.Number">
      <g :transform="isDesc ? 'translate(0, 6.6666)' : ''">
        <path d="M12.6667 4.00002C12.6667 3.26364 12.0697 2.66669 11.3333 2.66669C10.597 2.66669 10 3.26364 10 4.00002V5.33335C10 6.06973 10.597 6.66669 11.3333 6.66669C12.0697 6.66669 12.6667 6.06973 12.6667 5.33335V4.00002Z" stroke-linecap="round" stroke-linejoin="round"/>
      </g>
      <g :transform="isDesc ? 'translate(0, -6.6666)' : ''">
        <path d="M11.3333 13.3333V9.33331H10" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M10 13.3333H12.6667" stroke-linecap="round" stroke-linejoin="round"/>
      </g>
    </template>

    <!-- логический (Boolean) и дата (DateTime): полоски. Возрастание (от старых к новым) -
         короткая сверху, длинная снизу; убывание (от новых к старым) - длинная сверху,
         короткая снизу. -->
    <template v-else>
      <template v-if="!isDesc">
        <path d="M7.33337 2.66669H10" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M7.33337 5.33331H12" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M7.33337 8H14" stroke-linecap="round" stroke-linejoin="round"/>
      </template>
      <template v-else>
        <path d="M7.33337 8H10" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M7.33337 5.33337H12" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M7.33337 2.66669H14" stroke-linecap="round" stroke-linejoin="round"/>
      </template>
    </template>
  </svg>

  <!--
    Обычный двухстрелочный значок для типов без своей иконки. Активной (зелёной)
    становится стрелка текущего направления: левая вниз это возрастание (order=2),
    правая вверх это убывание (order=3). Без сортировки обе серые.
  -->
  <svg
    v-else
    class="qtl-sort-icon"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g :class="order === 2 ? 'qtl-sort-icon--active' : 'qtl-sort-icon--idle'">
      <path d="M2 10.6666L4.66667 13.3333L7.33333 10.6666" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M4.66669 13.3333V2.66663" stroke-linecap="round" stroke-linejoin="round"/>
    </g>
    <g :class="order === 3 ? 'qtl-sort-icon--active' : 'qtl-sort-icon--idle'">
      <path d="M14 5.33329L11.3334 2.66663L8.66669 5.33329" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M11.3333 2.66663V13.3333" stroke-linecap="round" stroke-linejoin="round"/>
    </g>
  </svg>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { TableHeaderCellDataType } from "@/models/etc/types";

const DT = TableHeaderCellDataType;

const props = defineProps<{
  /** тип данных колонки; от него зависит вид значка */
  dataType?: TableHeaderCellDataType;
  /** режим сортировки: 1 - нет, 2 - по возрастанию, 3 - по убыванию */
  order: number;
}>();

// Убывание (order 3): по нему меняем порядок правого глифа (цифры/буквы/полоски),
// стрелка при этом всегда смотрит вниз.
const isDesc = computed(() => props.order === 3);
// активна (зелёная), когда есть направление сортировки (возрастание или убывание)
const active = computed(() => props.order > 1);
// свой значок по типу показываем только в активном состоянии. Неактивный (серый)
// значок всегда обычный двухстрелочный, независимо от типа данных колонки.
// Дата (DateTime) использует тот же логический значок (полоски), что и Boolean.
const isTyped = computed(
  () =>
    active.value &&
    (props.dataType === DT.String ||
      props.dataType === DT.Number ||
      props.dataType === DT.Boolean ||
      props.dataType === DT.DateTime),
);
</script>

<style scoped lang="scss">
.qtl-sort-icon {
  display: block;
  flex-shrink: 0;
}

.qtl-sort-icon--active {
  stroke: #3f884f;
}

.qtl-sort-icon--idle {
  stroke: #979797;
}
</style>
